/**
 * The social vocabulary both ends share: friendship states, presence states,
 * and the snapshot shapes `/api/friends/*` returns and `SocialRoom` pushes.
 *
 * Presence is the one thing in this file with a privacy rule attached, and it is
 * worth stating where the type lives rather than only in the room that sends it:
 * a `PresenceState` says *what someone is doing*, never *where*. The room id is
 * deliberately absent from every shape below — the only client that ever learns
 * one is an invitee, through `room.invited`, which is a different message
 * altogether (design/v2.md §2.3, alignment Q5).
 */
/** The stored relationship states (design/v2.md §2.1). */
export const FRIEND_STATUSES = ['pending', 'accepted', 'declined', 'blocked'];
/**
 * What a friend may see about you. Three states plus `offline`, exactly as the
 * owner settled it: online (signed in, anywhere), lobby (browsing tables),
 * playing (at a table). `queued` is reserved for the matchmaking milestone and
 * is listed here so the client's exhaustive switch does not need rewriting when
 * it starts arriving.
 */
export const PRESENCE_STATES = ['offline', 'online', 'lobby', 'queued', 'playing'];
/**
 * A relationship as one side sees it. `blocked` and `blockedBy` are the two
 * halves of one stored row: symmetric in effect, asymmetric in who may lift it.
 */
export const FRIEND_RELATIONS = [
    'friends',
    'requestSent',
    'requestReceived',
    'declined',
    'blocked',
    'blockedBy',
    'self',
];
// ─────────────────────────────────────────────────────────────────── limits
/**
 * The caps of alignment Q4, in one place so the server's refusals and the
 * client's disabled states cannot drift.
 */
export const FRIEND_LIMITS = {
    /** Accepted friendships one account may hold. */
    maxFriends: 100,
    /** Outgoing requests that may be pending at once. */
    maxPendingOutgoing: 20,
    /** Requests per hour, enforced as an account-keyed rate limiter. */
    requestsPerHour: 10,
    /** A declined request cannot be repeated inside this window. */
    declineCooldownDays: 7,
};
export const DECLINE_COOLDOWN_MS = FRIEND_LIMITS.declineCooldownDays * 24 * 60 * 60 * 1000;
/** Minimum characters `/api/users/search` accepts, and how many it returns. */
export const USER_SEARCH_MIN_LENGTH = 3;
export const USER_SEARCH_LIMIT = 10;
/** How long a room invite stays acceptable (design/v2.md §2.4). */
export const ROOM_INVITE_TTL_MS = 120_000;
//# sourceMappingURL=social.js.map